"use client";

import type { CSSProperties } from "react";
import { useI18n } from "@/i18n/context";
import type { CaptchaStatus } from "./useCaptcha";

const NAVY = "#1E2939";
const GREEN = "#5C8A7D";
const RED = "#b4524a";

const box: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 14px",
  border: "1px solid rgba(30,41,57,0.18)",
  borderRadius: 10,
  background: "#fff",
  fontFamily: "var(--font-mono), monospace",
  fontSize: 11,
  letterSpacing: "0.04em",
  color: NAVY,
  lineHeight: 1,
};

/**
 * Visible anti-bot badge. Cosmetic by design: the actual check is an invisible
 * proof-of-work solved in the background — this just surfaces its state.
 */
export function CaptchaWidget({
  status,
  onRetry,
}: {
  status: CaptchaStatus;
  onRetry?: () => void;
}) {
  const { locale } = useI18n();
  const fr = locale === "fr";

  if (status === "ready") {
    return (
      <div style={box} aria-live="polite">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span>{fr ? "Vérification anti-robot ✓" : "Anti-bot check ✓"}</span>
      </div>
    );
  }

  if (status === "error") {
    return (
      <button type="button" onClick={onRetry} style={{ ...box, cursor: "pointer", color: RED }} aria-live="polite">
        <span>{fr ? "Échec de la vérification — réessayer" : "Verification failed — retry"}</span>
      </button>
    );
  }

  // solving
  return (
    <div style={box} aria-live="polite">
      <span
        className="wz-spin"
        style={{
          width: 14,
          height: 14,
          border: "2px solid rgba(30,41,57,0.25)",
          borderTopColor: NAVY,
          borderRadius: "50%",
          display: "inline-block",
        }}
      />
      <span style={{ opacity: 0.7 }}>{fr ? "Vérification anti-robot…" : "Anti-bot check…"}</span>
    </div>
  );
}
